import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SparklesIcon, ChartBarIcon, LightBulbIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import tradingScreen from '../assets/trading/stocks.png';
import advancedAnalysis from '../assets/trading/advanced-analysis.webp';
import botTrader from '../assets/trading/bot-trader.webp';
import futures from '../assets/trading/futures.webp';

const WealthSolutionsSection = () => {
  const solutions = [
    {
      icon: ChartBarIcon,
      title: "Advanced Trading Platform",
      description: "Professional-grade tools with real-time data, advanced charting, and institutional-level execution.",
      image: tradingScreen,
      features: [
        "Real-time market data",
        "Advanced order types",
        "Multi-asset trading",
        "Mobile & web platforms"
      ],
      link: "/exchange",
      color: "from-amber-500 to-orange-600"
    },
    {
      icon: SparklesIcon,
      title: "Automated Trading Bots",
      description: "Let AI-powered trading bots execute strategies 24/7 while you focus on what matters.",
      image: botTrader,
      features: [
        "Pre-built strategies",
        "Backtesting tools",
        "Risk management",
        "Performance analytics"
      ],
      link: "/futures-trading",
      color: "from-amber-500 to-orange-600"
    },
    {
      icon: LightBulbIcon,
      title: "Market Intelligence",
      description: "Access expert research, market insights, and real-time analytics to make informed decisions.",
      image: advancedAnalysis,
      features: [
        "Expert market analysis",
        "Technical indicators",
        "Sentiment analysis",
        "Price alerts"
      ],
      link: "/services/investment-management",
      color: "from-amber-500 to-orange-600"
    },
    {
      icon: ShieldCheckIcon,
      title: "Futures & Derivatives",
      description: "Trade perpetual futures with up to 100x leverage on major cryptocurrencies and assets.",
      image: futures,
      features: [
        "Up to 100x leverage",
        "Long & short positions",
        "Advanced risk tools",
        "24/7 trading"
      ],
      link: "/futures-trading",
      color: "from-amber-500 to-orange-600"
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white overflow-hidden">
      <div className="section-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 mb-6"
          >
            <SparklesIcon className="w-5 h-5 text-amber-400 mr-2" />
            <span className="text-sm font-semibold text-amber-400">Premium Solutions</span>
          </motion.div>

          <h2 className="text-3xl lg:text-5xl font-bold mb-6">
            Professional Trading Tools for Every Investor
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            From beginner-friendly interfaces to advanced institutional tools, we provide everything you need to succeed
          </p>
        </motion.div>

        {/* Solutions Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {solutions.map((solution, index) => {
            const IconComponent = solution.icon;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="relative bg-gray-800/50 rounded-3xl overflow-hidden border border-gray-700 hover:border-gray-600 transition-all duration-500 h-full">
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={solution.image}
                      alt={solution.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${solution.color} opacity-20 group-hover:opacity-30 transition-opacity duration-500`}></div>
                    
                    {/* Icon Badge */}
                    <div className="absolute top-4 right-4">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${solution.color} p-0.5`}>
                        <div className="w-full h-full bg-gray-900 rounded-xl flex items-center justify-center">
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8">
                    <h3 className="text-2xl font-bold mb-3">
                      {solution.title}
                    </h3>
                    <p className="text-gray-400 mb-6">
                      {solution.description}
                    </p>

                    {/* Features List */}
                    <div className="space-y-3 mb-6">
                      {solution.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center space-x-3">
                          <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${solution.color}`}></div>
                          <span className="text-sm text-gray-300">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <Link
                      to={solution.link}
                      className={`inline-flex items-center px-6 py-3 rounded-xl bg-gradient-to-r ${solution.color} text-white font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300`}
                    >
                      Explore Now
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <p className="text-gray-400 mb-6">
            Ready to unlock advanced trading features?
          </p>
          <a
            href="https://web3.blockfirstcapital.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 rounded-2xl bg-white text-gray-900 font-bold text-lg hover:bg-gray-100 transition-colors"
          >
            Get Started Today
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default WealthSolutionsSection;
